import { useTranslation } from 'react-i18next';
import { useProfile, useUpdateProfile, usePurchaseHistory } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { User, Mail, Phone, Building2, FileText, Download, LogOut, Edit3, Check, X } from 'lucide-react';

export function ProfilePage() {
  const { t } = useTranslation('profile');
  const { user, signOut } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: purchases } = usePurchaseHistory();
  const updateProfile = useUpdateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    company: profile?.company || '',
    bio: profile?.bio || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateProfile.mutateAsync(formData);
    setIsEditing(false);
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-aspexit-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-aspexit-gray">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-aspexit-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-aspexit-green" />
          </div>
          <p className="text-aspexit-gray">{t('notAuthenticated')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-aspexit-green/5 via-white to-aspexit-blue/5">
      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Header Section avec gradient */}
        <div className="bg-gradient-to-r from-aspexit-green to-aspexit-green-dark rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-extrabold mb-2">
                  {profile?.full_name || t('title')}
                </h1>
                <div className="flex items-center gap-2 text-white/90">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{user.email}</span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => signOut()}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('actions.signOut')}
            </Button>
          </div>
        </div>

        {/* Profile Information Card */}
        <Card className="p-8 mb-8 shadow-lg border-2 border-gray-100 hover:shadow-xl transition-shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-aspexit-black flex items-center gap-2">
              <FileText className="w-6 h-6 text-aspexit-green" />
              {t('fields.personalInfo')}
            </h2>
            <Button
              variant={isEditing ? "outline" : "default"}
              onClick={() => setIsEditing(!isEditing)}
              className={isEditing ? "" : "bg-aspexit-green hover:bg-aspexit-green-dark"}
            >
              {isEditing ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  {t('actions.cancel')}
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4 mr-2" />
                  {t('actions.edit')}
                </>
              )}
            </Button>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="full_name" className="mb-2 block flex items-center gap-2 text-aspexit-black font-semibold">
                  <User className="w-4 h-4 text-aspexit-green" />
                  {t('fields.fullName')}
                </Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Jean Dupont"
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="mb-2 block flex items-center gap-2 text-aspexit-black font-semibold">
                  <Phone className="w-4 h-4 text-aspexit-green" />
                  {t('fields.phone')}
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="company" className="mb-2 block flex items-center gap-2 text-aspexit-black font-semibold">
                  <Building2 className="w-4 h-4 text-aspexit-green" />
                  {t('fields.company')}
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Ma société"
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="bio" className="mb-2 block flex items-center gap-2 text-aspexit-black font-semibold">
                  <FileText className="w-4 h-4 text-aspexit-green" />
                  {t('fields.bio')}
                </Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  placeholder="Quelques mots sur vous..."
                  className="resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={updateProfile.isPending}
                  className="bg-aspexit-green hover:bg-aspexit-green-dark"
                  size="lg"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {updateProfile.isPending ? t('actions.saving') : t('actions.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  size="lg"
                >
                  {t('actions.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 text-aspexit-gray text-sm mb-2">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{t('fields.fullName')}</span>
                </div>
                <p className="text-aspexit-black font-semibold">
                  {profile?.full_name || <span className="text-aspexit-gray font-normal italic">{t('fields.notProvided')}</span>}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 text-aspexit-gray text-sm mb-2">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">{t('fields.phone')}</span>
                </div>
                <p className="text-aspexit-black font-semibold">
                  {profile?.phone || <span className="text-aspexit-gray font-normal italic">{t('fields.notProvided')}</span>}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 text-aspexit-gray text-sm mb-2">
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">{t('fields.company')}</span>
                </div>
                <p className="text-aspexit-black font-semibold">
                  {profile?.company || <span className="text-aspexit-gray font-normal italic">{t('fields.notProvided')}</span>}
                </p>
              </div>

              {profile?.bio && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 md:col-span-2">
                  <div className="flex items-center gap-2 text-aspexit-gray text-sm mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-medium">{t('fields.bio')}</span>
                  </div>
                  <p className="text-aspexit-black">{profile.bio}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Purchase History Card */}
        <Card className="p-8 shadow-lg border-2 border-gray-100 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-aspexit-green/10 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-aspexit-green" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-aspexit-black">
              {t('purchases.title')}
            </h2>
          </div>

          {purchases && purchases.length > 0 ? (
            <div className="space-y-4">
              {purchases.map((purchase: any) => (
                <div
                  key={purchase.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:border-aspexit-green/30 hover:shadow-md transition-all"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-aspexit-black mb-1">
                      {purchase.articles?.title || t('purchases.articleFallback')}
                    </h3>
                    <p className="text-sm text-aspexit-gray">
                      {new Date(purchase.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-aspexit-green text-white font-semibold px-4 py-1">
                      {purchase.amount_paid / 100}€
                    </Badge>
                    {purchase.pdf_url && (
                      <Button
                        size="sm"
                        asChild
                        className="bg-aspexit-green hover:bg-aspexit-green-dark"
                      >
                        <a href={purchase.pdf_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4 mr-2" />
                          {t('common.download', { ns: 'common' })}
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-aspexit-gray">{t('purchases.emptyState')}</p>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}
